/**
 * DisputesList - Payment disputes list with status and deadlines
 *
 * Features:
 * - Status badge with 'needs_response' highlighted
 * - Evidence deadline countdown
 * - Quick action to view/respond
 * - Loading skeleton state
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton } from '@respectly/ui';
import {
  AlertTriangle,
  Clock,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Hourglass,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDistanceToNow, differenceInDays, isPast } from 'date-fns';
import type { PaymentDispute, DisputeStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface DisputesListProps {
  disputes: PaymentDispute[];
  onViewDispute?: (id: string) => void;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

// ============================================
// Status Badge
// ============================================

const statusConfig: Record<
  DisputeStatus,
  { label: string; icon: typeof AlertTriangle; className: string }
> = {
  opened: {
    label: 'Opened',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  needs_response: {
    label: 'Needs Response',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  under_review: {
    label: 'Under Review',
    icon: Hourglass,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  won: {
    label: 'Won',
    icon: CheckCircle2,
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  lost: {
    label: 'Lost',
    icon: XCircle,
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  },
  accepted: {
    label: 'Accepted',
    icon: XCircle,
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  },
  withdrawn: {
    label: 'Withdrawn',
    icon: XCircle,
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  },
};

function DisputeStatusBadge({ status }: { status: DisputeStatus }) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        config.className
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

// ============================================
// Deadline Badge
// ============================================

function DeadlineBadge({ date }: { date: Date }) {
  const daysLeft = differenceInDays(date, new Date());
  const isOverdue = isPast(date);

  if (isOverdue) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 font-medium">
        <Clock className="h-3 w-3" />
        Overdue
      </span>
    );
  }

  const urgency =
    daysLeft <= 3
      ? 'text-red-600 dark:text-red-400'
      : daysLeft <= 7
        ? 'text-orange-600 dark:text-orange-400'
        : 'text-slate-500 dark:text-slate-400';

  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium', urgency)}>
      <Clock className="h-3 w-3" />
      {formatDistanceToNow(date, { addSuffix: true })}
    </span>
  );
}

// ============================================
// List Item
// ============================================

interface ListItemProps {
  dispute: PaymentDispute;
  onView?: () => void;
  index: number;
}

function DisputeListItem({ dispute, onView, index }: ListItemProps) {
  const needsAction = dispute.status === 'needs_response' || dispute.status === 'opened';

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer',
        needsAction
          ? 'bg-red-50/50 dark:bg-red-900/10 border-red-100 dark:border-red-900/20 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-800'
      )}
      onClick={onView}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center',
            needsAction
              ? 'bg-red-100 dark:bg-red-900/30'
              : 'bg-slate-100 dark:bg-slate-700'
          )}
        >
          <AlertTriangle
            className={cn(
              'h-4 w-4',
              needsAction
                ? 'text-red-600 dark:text-red-400'
                : 'text-slate-500 dark:text-slate-400'
            )}
          />
        </div>

        {/* Dispute info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {dispute.tenant?.name || 'Unknown Account'}
            </p>
            <DisputeStatusBadge status={dispute.status} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {dispute.reason}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Amount and deadline */}
        <div className="text-right">
          <p
            className={cn(
              'text-sm font-bold tabular-nums',
              needsAction
                ? 'text-red-600 dark:text-red-400'
                : 'text-slate-700 dark:text-slate-300'
            )}
          >
            {formatCurrency(dispute.amount)}
          </p>
          {dispute.evidenceDueBy && dispute.status === 'needs_response' && (
            <DeadlineBadge date={new Date(dispute.evidenceDueBy)} />
          )}
        </div>

        {/* Arrow */}
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </div>
    </motion.div>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
      </div>
      <p className="text-sm font-medium text-slate-900 dark:text-white">
        No disputes
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
        All payment disputes have been resolved
      </p>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function DisputesList({
  disputes,
  onViewDispute,
  isLoading,
  className,
}: DisputesListProps) {
  const needsResponseCount = disputes.filter(
    (d) => d.status === 'needs_response' || d.status === 'opened'
  ).length;

  if (isLoading) {
    return <DisputesListSkeleton className={className} />;
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-semibold">Payment Disputes</CardTitle>
            {disputes.length > 0 && (
              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300">
                {disputes.length}
              </span>
            )}
          </div>
          {needsResponseCount > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="font-semibold text-red-600 dark:text-red-400">
                {needsResponseCount}
              </span>
              <span className="text-slate-500">need response</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {disputes.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {/* Sort to show needs_response first */}
            {[...disputes]
              .sort((a, b) => {
                const aNeeds = a.status === 'needs_response' || a.status === 'opened';
                const bNeeds = b.status === 'needs_response' || b.status === 'opened';
                if (aNeeds && !bNeeds) return -1;
                if (!aNeeds && bNeeds) return 1;
                return 0;
                return 0;
              })
              .slice(0, 5)
              .map((dispute, index) => (
                <DisputeListItem
                  key={dispute.id}
                  dispute={dispute}
                  onView={() => onViewDispute?.(dispute.tenantId)}
                  index={index}
                />
              ))}
            {disputes.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-slate-600 hover:text-slate-900"
              >
                View all {disputes.length} disputes
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function DisputesListSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <div className="h-5 w-36 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3 rounded-lg bg-slate-100 dark:bg-slate-800"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="text-right">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
